import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { mediaUrl, PLACEHOLDER_COVER } from "@/lib/media";
import PortalShell from "@/portal/PortalShell";
import { usePortal } from "@/portal/PortalContext";
import { Button } from "@/portal/ui/Button";
import { PageHeader, EmptyState, ErrorState, Skeleton, Progress, Avatar, Fact, TitledPanel, IconTile } from "@/portal/ui/Layout";
import { Segmented } from "@/portal/ui/Tabs";
import { StatusBadge, PROJECT_STATUS } from "@/portal/ui/Badge";
import { fmtDateHuman, fmtDateLong, fmtTime } from "@/portal/format";
import { Search, FolderOpen, ArrowRight, X, Clock3, CalendarDays, Flag } from "lucide-react";

const FILTERS = [
  { value: "all", label: "Toate" },
  { value: "On Track", label: "În grafic" },
  { value: "Attention Required", label: "Necesită atenție" },
  { value: "Delayed", label: "Întârziat" },
  { value: "Completed", label: "Finalizat" },
];

export default function ClientProjects() {
  const { overview: ov, error, refresh } = usePortal();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const projects = ov?.projects || null;

  const counts = useMemo(() => {
    const c = { all: projects?.length || 0 };
    (projects || []).forEach((p) => { c[p.client_status] = (c[p.client_status] || 0) + 1; });
    return c;
  }, [projects]);

  const list = (projects || []).filter((p) =>
    (filter === "all" || p.client_status === filter) &&
    (!q || `${p.name} ${p.code} ${p.location}`.toLowerCase().includes(q.toLowerCase()))
  );
  const nextMeeting = ov?.upcoming_meetings?.[0];

  return (
    <PortalShell title="Proiecte" crumbs={[{ label: "Proiecte", to: "/portal/proiecte" }, { label: "Proiecte active" }]}>
      <PageHeader title="Proiectele tale" description="Monitorizează progresul, documentele și deciziile pentru toate proiectele tale." />

      {error && !ov ? <ErrorState onRetry={refresh} /> : !ov ? (
        <div className="space-y-4" aria-busy="true">{[0, 1].map((i) => <Skeleton key={i} className="h-[260px]" />)}</div>
      ) : projects.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Nu ai încă acces la niciun proiect" description="Accesul se acordă de echipa Brisk. Contactează Project Managerul tău dacă crezi că ar trebui să vezi un proiect aici." testid="no-projects" />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
            <Segmented ariaLabel="Filtrează după status" value={filter} onChange={setFilter}
              options={FILTERS.filter((f) => f.value === "all" || counts[f.value]).map((f) => ({ ...f, count: counts[f.value] || 0 }))} />
            <div className="relative sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cp-text-3 pointer-events-none" aria-hidden="true" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută proiecte..." aria-label="Caută proiect"
                className="cp-input !h-9 !pl-9 !pr-9" data-testid="projects-search" />
              {q && <button type="button" onClick={() => setQ("")} aria-label="Șterge căutarea" className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded text-cp-text-3 hover:text-cp-text"><X className="h-4 w-4" /></button>}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-5 items-start">
            <div className="space-y-4" data-testid="projects-grid">
              {list.length === 0 ? (
                <EmptyState compact title="Niciun proiect nu corespunde filtrului" description="Schimbă statusul sau șterge căutarea." testid="no-results" />
              ) : list.map((p, i) => <ProjectCard key={p.id} p={p} ov={ov} index={i} />)}
            </div>

            <TitledPanel title="Rezumat portofoliu" testid="portfolio-summary" className="xl:sticky xl:top-[calc(var(--cp-topbar-h)+24px)]">
              <ul className="space-y-4">
                <li className="flex items-center gap-3"><IconTile icon={FolderOpen} /><span className="text-[13.5px] text-cp-text-2 flex-1">Proiecte active</span><span className="cp-serif text-[22px] text-cp-text tabular-nums">{ov.stats.active}</span></li>
                <li className="flex items-center gap-3"><IconTile icon={Clock3} tone={ov.stats.pending_decisions ? "amber" : ""} /><span className="text-[13.5px] text-cp-text-2 flex-1">Decizii în așteptare</span><span className="cp-serif text-[22px] text-cp-text tabular-nums">{ov.stats.pending_decisions}</span></li>
                <li className="flex items-start gap-3"><IconTile icon={CalendarDays} /><div className="min-w-0"><div className="text-[13.5px] text-cp-text-2">Următoarea ședință</div>{nextMeeting ? <div className="text-[15px] font-semibold text-cp-text mt-0.5">{fmtDateLong(nextMeeting.when)}<br /><span className="font-normal text-cp-text-2">{fmtTime(nextMeeting.when)}</span></div> : <div className="cp-meta mt-0.5">Nicio ședință programată</div>}</div></li>
              </ul>
            </TitledPanel>
          </div>
        </>
      )}
    </PortalShell>
  );
}

function ProjectCard({ p, ov, index }) {
  const tone = p.client_status === "Delayed" ? "danger" : p.client_status === "Attention Required" ? "warning" : undefined;
  const decision = ov.pending_decisions.filter((a) => a.project_id === p.id).sort((a, b) => (a.deadline || "9").localeCompare(b.deadline || "9"))[0];
  const meeting = ov.upcoming_meetings.find((m) => m.project_id === p.id);
  const completion = p.estimated_completion || p.end_date;
  return (
    <article className={`cp-panel overflow-hidden cp-rise cp-rise-${Math.min(index, 4)}`} data-testid={`project-card-${p.code}`}>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,36%)_1fr] lg:grid-cols-[minmax(0,34%)_1fr_minmax(0,30%)]">
        <Link to={`/portal/proiecte/${p.id}`} className="block aspect-[16/10] md:aspect-auto md:h-full md:min-h-[150px] bg-cp-bg-subtle md:m-3 md:rounded-[var(--cp-radius)] overflow-hidden">
          <img src={mediaUrl(p.cover_image) || PLACEHOLDER_COVER} alt="" className="h-full w-full object-cover" loading="lazy" />
        </Link>
        <div className="p-5 min-w-0">
          <StatusBadge map={PROJECT_STATUS} value={p.client_status} size="sm" />
          <h2 className="cp-h2 mt-2.5 truncate"><Link to={`/portal/proiecte/${p.id}`} className="hover:text-cp-accent transition-colors">{p.name}</Link></h2>
          <div className="text-[13px] text-cp-text-2 mt-1 flex items-center gap-2 flex-wrap"><span>{p.code}</span>{p.current_stage && <><span className="text-cp-text-3">|</span><span>{p.current_stage}</span></>}</div>
          <div className="mt-5 grid grid-cols-2 gap-4">
            <Fact label="Finalizare estimată" value={fmtDateHuman(completion)} />
            {p.surface && <Fact label="Suprafață" value={p.surface} className="pl-4 border-l border-cp-border" />}
          </div>
        </div>
        <div className="p-5 pt-0 lg:pt-5 lg:pl-0 flex flex-col min-w-0">
          <div className="cp-meta mb-1.5">Progres</div>
          <div className="flex items-center gap-3"><Progress value={p.progress} tone={tone} className="flex-1" label={`Progres ${p.name}`} /><span className="text-[13px] font-semibold tabular-nums text-cp-text">{p.progress}%</span></div>
          {p.pm?.name && (
            <div className="mt-5">
              <div className="cp-meta mb-1.5">Manager de proiect</div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2.5 min-w-0"><Avatar name={p.pm.name} size="md" /><div className="min-w-0"><div className="text-[13.5px] font-semibold text-cp-text truncate">{p.pm.name}</div><div className="cp-meta truncate">{p.pm.job_title || "Project Manager"}</div></div></div>
                <Button as={Link} to={`/portal/proiecte/${p.id}`} size="sm" className="ml-auto" data-testid={`open-project-${p.code}`}>Vezi proiectul <ArrowRight /></Button>
              </div>
            </div>
          )}
          {!p.pm?.name && <div className="mt-auto pt-5 flex justify-end"><Button as={Link} to={`/portal/proiecte/${p.id}`} size="sm" data-testid={`open-project-${p.code}`}>Vezi proiectul <ArrowRight /></Button></div>}
        </div>
      </div>
      <div className="border-t border-cp-border px-5 py-4 bg-cp-surface">
        <div className="cp-h4 mb-3">Următorii pași</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-cp-border rounded-[var(--cp-radius)] border border-cp-border">
          <Step icon={Clock3} label="Decizie până la" value={decision?.deadline ? fmtDateLong(decision.deadline) : "Nicio decizie în așteptare"} accent={!!decision} to={decision ? `/portal/decizii?id=${decision.id}` : undefined} />
          <Step icon={CalendarDays} label="Ședință pe" value={meeting?.when ? <>{fmtDateLong(meeting.when)} <span className="font-normal text-cp-text-2 ml-2">{fmtTime(meeting.when)}</span></> : "Neprogramată"} to={meeting ? `/portal/proiecte/${p.id}?tab=calendar` : undefined} />
          <Step icon={Flag} label="Finalizare estimată" value={fmtDateHuman(completion)} to={`/portal/proiecte/${p.id}?tab=calendar`} />
        </div>
      </div>
    </article>
  );
}

function Step({ icon: Icon, label, value, accent, to }) {
  const inner = (
    <>
      <Icon className={`h-5 w-5 shrink-0 ${accent ? "text-cp-amber" : "text-cp-text-2"}`} aria-hidden="true" />
      <div className="min-w-0"><div className="cp-meta">{label}</div><div className={`text-[14px] font-semibold leading-snug ${accent ? "text-cp-amber" : "text-cp-text"}`}>{value}</div></div>
    </>
  );
  const cls = "flex items-center gap-3 px-4 py-3 min-w-0";
  return to ? <Link to={to} className={`${cls} cp-row-hover`}>{inner}</Link> : <div className={cls}>{inner}</div>;
}
